import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    Autocomplete,
    Popper,
    Collapse,
    FormControlLabel,
    Checkbox,
    MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DropzoneArea from '../DropzoneArea';
import MdbMissingAccountCardsTable from './MdbMissingAccountCardsTable';
import Constants from '../../../../assests/Constants/constants';
import {
    loadFinbotSettings,
    saveFinbotSettings,
    loadFinbotClientsCache,
    saveFinbotClientsCache,
    matchFinbotClientByCaseId,
} from './finbotImportStorage';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box',
});

const StyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: 9999,
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

const FinbotJournalImportModal = ({
    open,
    onClose,
    previewFinbotJournal,
    commitFinbotJournal,
    previewFinbotJournalApi,
    commitFinbotJournalApi,
    fetchFinbotClients,
    createMissingSortCodes,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    client,
    token,
    palCodes = [],
    accountCardOptions = [],
    fetchEnums,
    setLoading,
    user,
    setAccountCardOptions
}) => {
    const [importMode, setImportMode] = useState('file'); // 'file' | 'api'
    const [journalFile, setJournalFile] = useState(null);
    const [isTempJournal, setIsTempJournal] = useState(false);
    const [downloadDocuments, setDownloadDocuments] = useState(false);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    // API-mode credentials / range: prefilled from localStorage so they carry across clients.
    const storedSettings = useMemo(() => loadFinbotSettings(), []);
    const [finbotToken, setFinbotToken] = useState(storedSettings.token || '');
    const [finbotUserAccountId, setFinbotUserAccountId] = useState('');
    const [subdomain, setSubdomain] = useState(storedSettings.subdomain || '');
    const [fromDate, setFromDate] = useState(storedSettings.fromDate || '');
    const [toDate, setToDate] = useState(storedSettings.toDate || '');
    // Regular journal date field: 'vatDate' (תאריך מע"מ) or 'referenceDate' (תאריך אסמכתא).
    const [dateType, setDateType] = useState(storedSettings.dateType || 'vatDate');
    // Temp journal date field: 'documentDate' (default, draft entries have no vatDate) or 'referenceDate'.
    const [tempDateType, setTempDateType] = useState(storedSettings.tempDateType || 'documentDate');

    // Finbot client picker (the loaded list is cached so auto-matching works without re-loading)
    const [clients, setClients] = useState(() => loadFinbotClientsCache());
    const [selectedClient, setSelectedClient] = useState(null);
    const [loadingClients, setLoadingClients] = useState(false);

    // Persist credentials/range whenever they change, so the next client opens prefilled.
    useEffect(() => {
        saveFinbotSettings({ token: finbotToken, subdomain, fromDate, toDate, dateType, tempDateType });
    }, [finbotToken, subdomain, fromDate, toDate, dateType, tempDateType]);

    // Cache the loaded client list for reuse across clients.
    useEffect(() => {
        saveFinbotClientsCache(clients);
    }, [clients]);

    // Auto-resolve the Finbot account id for the current client by matching its caseId against the
    // Finbot client companyId, so the user does not have to pick it manually.
    useEffect(() => {
        if (importMode !== 'api' || !clients.length) return;
        const match = matchFinbotClientByCaseId(clients, client?.caseId);
        if (match && match.id != null) {
            setSelectedClient(match);
            setFinbotUserAccountId(String(match.id));
        }
    }, [clients, client?.caseId, importMode]);

    const dateToMs = (dateStr, endOfDay = false) => {
        if (!dateStr) return null;
        const ms = new Date(`${dateStr}T${endOfDay ? '23:59:59.999' : '00:00:00'}`).getTime();
        return Number.isNaN(ms) ? null : ms;
    };

    // The id used for the actual import: the selected client, falling back to the typed id.
    const effectiveAccountId = (selectedClient?.id ?? String(finbotUserAccountId)).trim();

    // Loading the client list only needs the token + subdomain: the account id is what we are
    // trying to discover from that list, so it must not be required here.
    const credentialsReady = !!(finbotToken.trim() && subdomain.trim());

    const handleLoadClients = async () => {
        if (!credentialsReady || !fetchFinbotClients) return;
        try {
            setLoadingClients(true);
            const list = await fetchFinbotClients({
                finbotToken: finbotToken.trim(),
                finbotUserAccountId: String(finbotUserAccountId).trim(),
                subdomain: subdomain.trim(),
            });
            setClients(Array.isArray(list) ? list : []);
        } catch {
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    };

    const buildApiParams = () => ({
        finbotToken: finbotToken.trim(),
        finbotUserAccountId: effectiveAccountId,
        subdomain: subdomain.trim(),
        fromDate: dateToMs(fromDate, false),
        toDate: dateToMs(toDate, true),
        isTempJournal,
        downloadDocuments,
        dateType,
        tempDateType,
    });

    const apiFieldsReady = !!(
        finbotToken.trim() &&
        effectiveAccountId &&
        subdomain.trim() &&
        fromDate &&
        toDate
    );

    const inputReady = importMode === 'api' ? apiFieldsReady : !!journalFile;

    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [editableMissing, setEditableMissing] = useState([]);
    const [parsedRows, setParsedRows] = useState(0);
    const [showMissingSection, setShowMissingSection] = useState(false);

    // Parsed journal entries (transactions read from the file)
    const [entries, setEntries] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('documentDate');
    const [order, setOrder] = useState('asc');
    const [entriesSearch, setEntriesSearch] = useState('');

    // Bulk edit state
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);

    const vatOptions = useMemo(
        () => (Constants.VATPercentage || []).map(v => ({ label: `${v}%`, value: v })),
        []
    );
    const parOptions = useMemo(
        () => [{ label: t('Yes'), value: true }, { label: t('No'), value: false }],
        [t]
    );

    // Resolve account card id → display name (for the transactions preview).
    const cardIdToName = useMemo(() => {
        const m = new Map();
        (accountCardOptions || [])
            .filter((o) => o.value1 !== 'AddItem')
            .forEach((o) => {
                const id = o.cardId ?? o.value1 ?? o.value;
                if (id == null || id === '') return;
                const strId = String(id).trim();
                const name = String(o.name ?? o.label ?? o.accountCaseId ?? '').trim() || strId;
                m.set(strId, name);
                if (/^\d+$/.test(strId)) m.set(Number(strId), name);
            });
        return m;
    }, [accountCardOptions]);

    const resolveAccountDisplay = useCallback((id, fileName) => {
        if (id == null || id === '') return '—';
        const strId = String(id).trim();
        const name = (fileName && fileName.trim())
            || cardIdToName.get(strId)
            || cardIdToName.get(Number(strId))
            || '';
        return name ? `${name} (${strId})` : strId;
    }, [cardIdToName]);

    const handleJournalFileDrop = files => {
        if (files?.length) setJournalFile(files[0]);
    };

    const handlePreview = async () => {
        if (!inputReady) return;

        try {
            setProcessing(true);
            setPreviewDone(false);

            const res = importMode === 'api'
                ? await previewFinbotJournalApi(buildApiParams())
                : await previewFinbotJournal(journalFile);

            const missing = res?.missingSortCodes ?? [];
            const missingCards = res?.missingAccountCards ?? [];
            const journalEntries = res?.journalEntries ?? [];
            const rows = res?.parsedRows ?? 0;

            // Normalize missing cards to { cardId, cardName } (backend returns names from the file)
            setMissingAccountCards(missingCards.map(card => (
                typeof card === 'string'
                    ? { cardId: card, cardName: '' }
                    : { cardId: card.cardId ?? card.CardId ?? '', cardName: card.cardName ?? card.CardName ?? '' }
            )));
            setEntries(journalEntries);
            setParsedRows(rows);

            // Normalize missing sortcodes
            setEditableMissing(missing.map(sc => ({
                ...sc,
                sortCodeId: sc.sortCodeId || '',
                sortCodeName: sc.sortCodeName || '',
                sortCodeVatType: sc.sortCodeVatType ?? null,
                sortCodeCategory: sc.sortCodeCategory ?? null,
                participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
                vat: sc.vat ?? 0
            })));

            setPage(0);
            setOrderBy('documentDate');
            setOrder('asc');
            setEntriesSearch('');
            setShowMissingSection(false);
            setPreviewDone(true);
        } catch (err) {
            console.error('Finbot journal preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!inputReady) return;

        try {
            setProcessing(true);

            if (editableMissing && editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
            }

            const res = importMode === 'api'
                ? await commitFinbotJournalApi(buildApiParams())
                : await commitFinbotJournal(journalFile, isTempJournal);

            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Finbot journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing(prev =>
            prev.map(r =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkVatType ? bulkVatType.value : r.sortCodeVatType,
                        sortCodeCategory: bulkGroup ? bulkGroup.value : r.sortCodeCategory,
                        participatesInProfitAndLoss: bulkPar ? bulkPar.value : r.participatesInProfitAndLoss,
                        vat: bulkVat ? bulkVat.value : r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
    };

    const missingIncomplete = useMemo(
        () => editableMissing.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null ||
            sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [editableMissing]
    );

    const formatAmount = (amount) => {
        if (amount === null || amount === undefined) return '';
        const num = typeof amount === 'number' ? amount : parseFloat(amount);
        if (Number.isNaN(num)) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yy = String(d.getFullYear()).slice(-2);
            return `${dd}/${mm}/${yy}`;
        } catch {
            return '';
        }
    };

    const getComparator = (ord, key) => (a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
        if (ord === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    };

    const entrySearchHaystack = useCallback((e) => [
        formatDate(e.documentDate),
        e.transactionType,
        e.reference,
        e.reference2,
        e.debitAccount,
        e.debitAccountName,
        e.creditAccount,
        e.creditAccountName,
        e.amount,
        e.vat,
        e.description,
    ]
        .filter((v) => v !== null && v !== undefined && v !== '')
        .join(' ')
        .toLowerCase(), []);

    const filteredEntries = useMemo(() => {
        const q = entriesSearch.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e) => entrySearchHaystack(e).includes(q));
    }, [entries, entriesSearch, entrySearchHaystack]);

    const sortedEntries = useMemo(() => {
        if (!filteredEntries.length) return [];
        return [...filteredEntries].sort(getComparator(order, orderBy));
    }, [filteredEntries, order, orderBy]);

    const paginatedEntries = useMemo(
        () => sortedEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedEntries, page, rowsPerPage]
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const renderSortHeader = (id, label, align = 'left') => (
        <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const handleClose = () => {
        setJournalFile(null);
        setImportMode('file');
        // Keep token / subdomain / dates / loaded clients / selection so they carry to the next
        // client (persisted in localStorage); only the per-import view state is reset below.
        setLoadingClients(false);
        setIsTempJournal(false);
        setDownloadDocuments(false);
        setPreviewDone(false);
        setMissingAccountCards([]);
        setEditableMissing([]);
        setParsedRows(0);
        setSelectedRows([]);
        setEntries([]);
        setPage(0);
        setEntriesSearch('');
        setShowMissingSection(false);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1200px' : '600px' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('ImportFinbotJournalEntries') || 'ייבוא פקודות יומן מ-Finbot'}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Import mode toggle: file vs API */}
                        <Box display="flex" gap={1}>
                            <Button
                                size="small"
                                variant={importMode === 'file' ? 'contained' : 'outlined'}
                                onClick={() => setImportMode('file')}
                                disabled={processing}
                            >
                                {t('ImportFromFile') || 'מקובץ'}
                            </Button>
                            <Button
                                size="small"
                                variant={importMode === 'api' ? 'contained' : 'outlined'}
                                onClick={() => setImportMode('api')}
                                disabled={processing}
                            >
                                {t('ImportFromApi') || 'מ-API'}
                            </Button>
                        </Box>

                        {importMode === 'file' ? (
                            /* Journal File Upload */
                            <div>
                                <h5>{t('JournalEntriesFile') || 'קובץ פקודות יומן'} (.xlsx/.xls)</h5>
                                <DropzoneArea
                                    key={journalFile ? journalFile.name : 'finbot-journal-empty'}
                                    onDrop={handleJournalFileDrop}
                                    accept=".xls,.xlsx"
                                    filesLimit={1}
                                />
                                {journalFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {journalFile.name}
                                    </Typography>
                                )}
                            </div>
                        ) : (
                            /* API credentials + date range */
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <TextField
                                    label={t('FinbotToken') || 'Finbot Token'}
                                    value={finbotToken}
                                    onChange={(e) => setFinbotToken(e.target.value)}
                                    size="small"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    placeholder={t('FinbotTokenPlaceholder') || 'הדבק כאן את ה-JWT מ-Finbot'}
                                />
                                <Box display="flex" gap={1.5} flexWrap="wrap">
                                    <TextField
                                        label={t('FinbotUserAccountId') || 'Finbot Account Id'}
                                        value={finbotUserAccountId}
                                        onChange={(e) => setFinbotUserAccountId(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 160 }}
                                    />
                                    <TextField
                                        label={t('FinbotSubdomain') || 'Subdomain'}
                                        value={subdomain}
                                        onChange={(e) => setSubdomain(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 160 }}
                                        placeholder="zuab"
                                    />
                                </Box>

                                {/* Client picker: load and search the accountant's Finbot clients */}
                                <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleLoadClients}
                                        disabled={!credentialsReady || loadingClients}
                                    >
                                        {loadingClients ? (t('Processing') || 'Processing') : (t('LoadFinbotClients') || 'טען לקוחות')}
                                    </Button>
                                    <Autocomplete
                                        options={clients}
                                        loading={loadingClients}
                                        value={selectedClient}
                                        onChange={(e, newValue) => {
                                            setSelectedClient(newValue);
                                            if (newValue?.id) setFinbotUserAccountId(String(newValue.id));
                                        }}
                                        getOptionLabel={(o) =>
                                            o ? `${o.name || ''}${o.companyId ? ` - ${o.companyId}` : ''}` : ''
                                        }
                                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                        noOptionsText={t('NoFinbotClients') || 'אין לקוחות (טען קודם)'}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('SelectFinbotClient') || 'חפש/בחר לקוח'}
                                                size="small"
                                            />
                                        )}
                                        PopperComponent={StyledPopper}
                                        sx={{ flex: 1, minWidth: 220 }}
                                    />
                                </Box>

                                <Box display="flex" gap={1.5} flexWrap="wrap">
                                    <TextField
                                        select
                                        label={t('FinbotDateTypeRegular') || 'סינון פקודות רגילות'}
                                        value={dateType}
                                        onChange={(e) => setDateType(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 160 }}
                                    >
                                        <MenuItem value="vatDate">{t('VatDate') || 'תאריך מע"מ'}</MenuItem>
                                        <MenuItem value="referenceDate">{t('ReferenceDate') || 'תאריך אסמכתא'}</MenuItem>
                                    </TextField>
                                    <TextField
                                        select
                                        label={t('FinbotDateTypeTemp') || 'סינון פקודות זמניות'}
                                        value={tempDateType}
                                        onChange={(e) => setTempDateType(e.target.value)}
                                        size="small"
                                        sx={{ flex: 1, minWidth: 160 }}
                                    >
                                        <MenuItem value="documentDate">{t('DocumentDate') || 'תאריך מסמך'}</MenuItem>
                                        <MenuItem value="referenceDate">{t('ReferenceDate') || 'תאריך אסמכתא'}</MenuItem>
                                    </TextField>
                                </Box>

                                <Box display="flex" gap={1.5} flexWrap="wrap">
                                    <TextField
                                        label={t('FromDate') || 'מתאריך'}
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ flex: 1, minWidth: 160 }}
                                    />
                                    <TextField
                                        label={t('ToDate') || 'עד תאריך'}
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ flex: 1, minWidth: 160 }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {importMode === 'file' && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isTempJournal}
                                        onChange={(e) => setIsTempJournal(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('ImportAsTempJournal')}
                            />
                        )}

                        {importMode === 'api' && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={downloadDocuments}
                                        onChange={(e) => setDownloadDocuments(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('ImportWithDocuments') || 'Import with documents'}
                            />
                        )}

                        <Box display="flex" gap={1.5} mt={1}>
                            <Button
                                variant="contained"
                                onClick={handlePreview}
                                disabled={!inputReady || processing}
                            >
                                {processing && !previewDone ? t('Processing') : t('Preview')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                disabled={!previewDone || processing || missingIncomplete || missingAccountCards.length > 0}
                                onClick={handleCreateAndCommit}
                            >
                                {processing && previewDone ? t('Processing') : t('CreateAndImport')}
                            </Button>
                        </Box>
                    </Box>

                    {previewDone && (
                        <>
                            <Box mt={2.5}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{parsedRows}</strong>
                                </Typography>
                            </Box>

                            {/* Missing Sortcodes – collapsible section above the table */}
                            {editableMissing.length > 0 ? (
                                <Box mt={2.5}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{
                                            padding: 2,
                                            backgroundColor: missingIncomplete ? 'action.hover' : 'grey.100',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: missingIncomplete ? 'warning.main' : 'divider',
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {t('MissingSortCodes')}: <strong>{editableMissing.length}</strong>
                                            {missingIncomplete && (
                                                <Typography component="span" variant="body2" color="warning.dark" sx={{ ml: 1 }}>
                                                    {t('FillRequiredFields') || ''}
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setShowMissingSection(prev => !prev)}
                                            endIcon={showMissingSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        >
                                            {showMissingSection ? (t('Hide') || 'Hide') : (t('Open') || 'Open')}
                                        </Button>
                                    </Box>
                                    <Collapse in={showMissingSection}>
                                        <Box mt={2}>
                                            {/* Bulk Edit Controls */}
                                            <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={2}>
                                                <Autocomplete
                                                    options={formattedSortCodeVatType || []}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkVatType}
                                                    onChange={(e, newValue) => setBulkVatType(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('SelectSortCodeVatType')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Autocomplete
                                                    options={formattedSortCodesGroupNamesOptions || []}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkGroup}
                                                    onChange={(e, newValue) => setBulkGroup(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Autocomplete
                                                    options={parOptions}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkPar}
                                                    onChange={(e, newValue) => setBulkPar(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('participatesInProfitAndLoss')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 140 }}
                                                />
                                                <Autocomplete
                                                    options={vatOptions}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkVat}
                                                    onChange={(e, newValue) => setBulkVat(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('VAT')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 100 }}
                                                />
                                                <BorderBoxButton
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={!bulkVatType && !bulkGroup && !bulkPar && !bulkVat}
                                                    onClick={applyBulk}
                                                >
                                                    {t('ApplyToSelected')}
                                                </BorderBoxButton>
                                            </Box>

                                            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                <Table size="small" stickyHeader className="sortcode-table editable">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12, width: '5%' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                                                    onChange={e => setSelectedRows(e.target.checked ? editableMissing.map(r => r.sortCodeId) : [])}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode Id')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode Name')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SelectSortCodeVatType')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SelectSortCodeGroupName')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('participatesInProfitAndLoss')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('VAT')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {editableMissing.map((sc, i) => (
                                                            <TableRow key={sc.sortCodeId}>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedRows.includes(sc.sortCodeId)}
                                                                        onChange={e => setSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        value={sc.sortCodeId}
                                                                        variant="standard"
                                                                        InputProps={{ readOnly: true, disableUnderline: true }}
                                                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        value={sc.sortCodeName || ''}
                                                                        variant="standard"
                                                                        onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                                        placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                                        error={!sc.sortCodeName || !sc.sortCodeName.trim()}
                                                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={formattedSortCodeVatType || []}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'sortCodeVatType', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder={t('SelectSortCodeVatType')}
                                                                                size="small"
                                                                                error={sc.sortCodeVatType == null}
                                                                            />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 160 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={formattedSortCodesGroupNamesOptions || []}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeCategory) || null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'sortCodeCategory', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 160 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={parOptions}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={sc.participatesInProfitAndLoss === true ? parOptions[0] : sc.participatesInProfitAndLoss === false ? parOptions[1] : null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'participatesInProfitAndLoss', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder={t('participatesInProfitAndLoss')}
                                                                                size="small"
                                                                                error={sc.participatesInProfitAndLoss == null}
                                                                            />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 120 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={vatOptions}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={vatOptions.find(opt => opt.value === sc.vat) || null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'vat', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder={t('VAT')}
                                                                                size="small"
                                                                                error={sc.vat == null}
                                                                            />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 100 }}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Collapse>
                                </Box>
                            ) : (
                                <Box mt={2} p={1.5} bgcolor="success.light" borderRadius={1} border="1px solid" borderColor="success.main">
                                    <Typography variant="body2" color="success.dark">
                                        ✓ {t('NoMissingSortCodes') || 'אין סוגי תנועות חסרים'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Missing Account Cards */}
                            {missingAccountCards.length > 0 && (
                                <Box mt={2.5}>
                                    <MdbMissingAccountCardsTable
                                        missingAccountCards={missingAccountCards}
                                        direction={direction}
                                        client={client}
                                        token={token}
                                        palCodes={palCodes || []}
                                        accountCardOptions={accountCardOptions}
                                        fetchEnums={fetchEnums}
                                        setLoading={setLoading}
                                        isRange={false}
                                        isClosedReportingMonth={false}
                                        sortCodes={[]}
                                        user={user}
                                        setAccountCardOptions={setAccountCardOptions}
                                        onCardCreated={async () => {
                                            if (journalFile) {
                                                await handlePreview();
                                            }
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Parsed Transactions Preview */}
                            {entries.length > 0 && (
                                <Box mt={3} sx={{ maxHeight: 520, overflow: 'auto' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {t('JournalEntries')} ({filteredEntries.length}{entriesSearch.trim() ? ` / ${entries.length}` : ''})
                                        </Typography>
                                        <TextField
                                            size="small"
                                            placeholder={t('Search')}
                                            value={entriesSearch}
                                            onChange={(ev) => {
                                                setEntriesSearch(ev.target.value);
                                                setPage(0);
                                            }}
                                            InputProps={{
                                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />,
                                            }}
                                            sx={{ minWidth: 220 }}
                                        />
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {renderSortHeader('documentDate', t('DocumentDate'))}
                                                    {renderSortHeader('transactionType', t('TransactionType') || 'סוג תנועה')}
                                                    {renderSortHeader('reference', t('Reference'))}
                                                    {renderSortHeader('debitAccount', t('DebitAccount'))}
                                                    {renderSortHeader('creditAccount', t('CreditAccount'))}
                                                    {renderSortHeader('amount', t('Amount'), 'right')}
                                                    {renderSortHeader('vat', t('VAT'), 'right')}
                                                    {renderSortHeader('description', t('Description'))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedEntries.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                            {t('NoResults') || t('Search')}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : paginatedEntries.map((e, idx) => (
                                                    <TableRow key={`${e.reference ?? ''}-${e.amount}-${idx}`} hover>
                                                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(e.documentDate)}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{e.transactionType || '—'}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{e.reference || ''}</TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {resolveAccountDisplay(e.debitAccount, e.debitAccountName)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {resolveAccountDisplay(e.creditAccount, e.creditAccountName)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(e.amount)}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>
                                                            {e.vat !== null && e.vat !== undefined ? `${formatAmount(e.vat)}%` : ''}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }} title={e.description}>
                                                            {e.description || ''}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={sortedEntries.length}
                                            page={page}
                                            onPageChange={(_, p) => setPage(p)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                        />
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinbotJournalImportModal;
